import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Input } from "../../lib/Input";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";
import { intake } from "../../lib/api";
import { withUtm } from "../../lib/utm";
import { breadcrumbList, faqJsonLd, useSeo } from "../../lib/useSeo";

// Named incumbents that force their own processor + take a margin — sourced
// from data/competitors.ts (SoftwareConnect / Self-Storage Talk / Capterra).
const OFFENDERS = [
  {
    name: "storEDGE / Storable",
    detail: "Storable Payments is non-optional — you can't bring your own processor.",
  },
  {
    name: "SiteLink",
    detail: "Payments route through SiteLink Merchant Services with a margin on every charge.",
  },
  {
    name: "Easy Storage Solutions",
    detail: "Locks you into ESS-mandated merchant rates — no processor choice.",
  },
];

const FAQ: [string, string][] = [
  [
    "Doesn't every software take payment processing fees?",
    "Stripe charges its standard processing fee (about 2.9% + 30¢) — that's the card networks, and it's the same everywhere. The difference is the platform margin on top. Incumbents force their own processor and add their cut, which comes out of your rent. NestPlot uses your own Stripe account and adds 0% — you pay Stripe directly, and nobody sits in the middle skimming.",
  ],
  [
    "How does 'bring your own Stripe' actually work?",
    "You connect your own Stripe account to NestPlot (Stripe Connect). Tenant payments settle directly into your bank on Stripe's normal payout schedule. NestPlot never holds your money, never takes a percentage, and can't freeze your deposits.",
  ],
  [
    "So how does NestPlot make money?",
    "A flat monthly software fee — Starter $59, Growth $99, Portfolio $199 — and that's it. We're paid for the software, not for standing between you and your tenants' payments. Our incentives and yours point the same way.",
  ],
  [
    "What if I already have a merchant account I like?",
    "Stripe is what NestPlot connects to today. If you're coming off a forced-processor incumbent, moving to your own Stripe almost always lowers your effective rate and ends the deposit-hold games — which is the whole point.",
  ],
];

export function KeepYourRentPage() {
  useSeo({
    title: "Keep 100% of your rent — storage software that takes 0% of tenant payments",
    description:
      "Your storage software shouldn't take a cut of your rent. NestPlot routes every tenant payment through your own Stripe — money lands in your bank, NestPlot takes 0%. Flat from $59/mo. Unlike storEDGE, SiteLink, and Easy Storage Solutions, no forced processor.",
    path: "/keep-100-of-your-rent",
    jsonLd: [
      breadcrumbList([
        { name: "NestPlot", path: "/" },
        { name: "Keep 100% of your rent", path: "/keep-100-of-your-rent" },
      ]),
      faqJsonLd(FAQ.map(([q, a]) => ({ q, a }))),
    ],
  });

  return (
    <div className="mx-auto max-w-[1080px] px-5 lg:px-8 py-10 lg:py-16">
      {/* Hero */}
      <div className="max-w-[820px]">
        <Pill tone="accent" size="sm">
          0% of your customer rent · bring your own Stripe
        </Pill>
        <h1 className="t-display-xl mt-4">
          Your storage software shouldn't take a cut of your rent.
        </h1>
        <p className="text-sub text-lg mt-4">
          Most storage platforms force you onto their payment processor and keep
          a margin on every tenant charge — money skimmed off your revenue before
          it ever reaches your bank. NestPlot doesn't. Every dollar your tenants
          pay runs through <strong>your own</strong> Stripe account and lands
          directly in your bank. We take 0% — by design, we can't take a cut.
        </p>
        {/* "Start free" stays the lead here — keeping 100% of your rent is a
            claim somebody can verify alone in ten minutes. The second button
            used to hop to another landing page, which is a page view, not a
            decision. Fifteen minutes with a person is the thing that actually
            moves an operator who is weighing a switch, and the ads into this
            page produced no sign-ups on self-serve alone. */}
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/signup">
            <Btn kind="primary" size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
              Start free
            </Btn>
          </Link>
          <Link to="/book-demo">
            <Btn kind="secondary" size="lg">
              Book a 15-minute walkthrough
            </Btn>
          </Link>
        </div>
        <p className="text-sub text-xs mt-3">Flat from $59/mo. 30-day trial, no card.</p>
      </div>

      {/* The skim, visualized */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <div className="px-6 py-6">
              <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
                The incumbent model
              </div>
              <div className="font-display font-extrabold text-[18px] mt-2 text-alert">
                They sit between you and your money.
              </div>
              <ul className="mt-4 space-y-2 text-sm text-sub">
                {[
                  "Their processor is mandatory — no choice",
                  "A platform margin on top of card fees",
                  "Deposits can be held on their schedule",
                  "The bill grows per facility and per user",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Icon name="close" size={14} className="text-alert mt-[3px] shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Card>
          <Card className="shadow-rollup" style={{ border: "1.5px solid var(--accent)" }}>
            <div className="bg-accent px-6 pt-3 pb-2">
              <Slats color="var(--on-accent)" opacity={0.55} height={10} />
            </div>
            <div className="px-6 py-6">
              <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-accent">
                The NestPlot model
              </div>
              <div className="font-display font-extrabold text-[18px] mt-2">
                Your Stripe. Your bank. Your money.
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {[
                  "Bring your own Stripe Connect account",
                  "NestPlot takes 0% of tenant rent",
                  "Payments settle straight to your bank",
                  "One flat software fee — that's all we charge",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Icon name="check" size={14} className="text-ok mt-[3px] shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      </section>

      {/* Illustrative math */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="rounded-xl bg-surface border border-line px-6 py-8 lg:px-10 lg:py-10">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            What the margin costs you
          </div>
          <h2 className="t-display-l mt-2 max-w-[760px]">
            A point or two on your rent adds up fast.
          </h2>
          <p className="text-sub mt-3 max-w-[760px]">
            Say a facility collects <strong>$45,000/month</strong> in rent. A
            platform margin of just <strong>1%</strong> on top of card processing
            is <strong>$450 a month — about $5,400 a year</strong> quietly taken
            off your top line. On NestPlot that margin is zero, and the software
            itself is a flat $59–$199. The bigger you get, the more the skim costs
            you — and the more NestPlot's flat pricing wins.
          </p>
          <p className="text-sub text-xs mt-3">
            Illustrative — your rent volume and your incumbent's exact margin will
            differ. The point holds: a percentage of your rent is money you keep
            with NestPlot.
          </p>
        </div>
      </section>

      {/* Who forces a processor */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Who does this
        </div>
        <h2 className="t-display-l mt-2">The storage platforms that hold your payments</h2>
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {OFFENDERS.map((o) => (
            <Card key={o.name}>
              <div className="px-5 py-5">
                <div className="font-display font-extrabold text-[16px] tracking-[-0.01em]">
                  {o.name}
                </div>
                <p className="text-sub text-sm mt-2">{o.detail}</p>
              </div>
            </Card>
          ))}
        </div>
        <p className="text-sub text-xs mt-4">
          Sources: SoftwareConnect, Self-Storage Talk operator threads, Capterra.
          On {" "}
          <Link to="/vs" className="text-accent font-semibold">
            every NestPlot comparison
          </Link>
          , the payments row is the same story.
        </p>
      </section>

      {/* FAQ */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Straight answers
        </div>
        <h2 className="t-display-l mt-2">The honest payments FAQ</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-3">
          {FAQ.map(([q, a]) => (
            <Card key={q}>
              <div className="px-5 py-4">
                <div className="font-semibold">{q}</div>
                <p className="text-sub text-sm mt-2">{a}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Lead + CTA */}
      <section className="mt-14 border-t border-line pt-12">
        <div className="grid md:grid-cols-[1.1fr_1fr] gap-8 items-start">
          <div>
            <h2 className="t-display-l">Stop funding someone else's margin.</h2>
            <p className="text-sub mt-3">
              Move to NestPlot and route your rent through your own Stripe. Start
              free in minutes, or tell us about your setup and we'll map the switch.
            </p>
            <div className="mt-5">
              <Link to="/signup">
                <Btn kind="primary" size="md">
                  Start free now
                </Btn>
              </Link>
            </div>
          </div>
          <KeepRentLeadForm />
        </div>
      </section>
    </div>
  );
}

function KeepRentLeadForm() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [facilities, setFacilities] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      await intake.submitInquiry(
        withUtm({
          name: name.trim(),
          email: email.trim(),
          company: company.trim() || undefined,
          facility_count: facilities ? Number(facilities) : undefined,
          message: "Interested in keeping 100% of rent (bring-your-own-Stripe).",
          source: "keep-your-rent",
        }),
      );
      setSubmitted(true);
      toast.success("Got it — we'll be in touch within one business day.");
    } catch {
      toast.error("Send failed. Email info@nestplot.com directly.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Card>
        <div className="px-6 py-8 text-center">
          <Icon name="check" size={22} className="text-ok mx-auto" />
          <div className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em] mt-2">
            Thanks — we got it.
          </div>
          <p className="text-sub text-sm mt-2">
            We'll reply within one business day.{" "}
            <button
              type="button"
              onClick={() => navigate("/signup")}
              className="text-accent font-semibold"
            >
              Or start free now →
            </button>
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="px-6 py-6 space-y-4">
        <div className="grid gap-4">
          <Input label="Your name" value={name} onChange={(e) => setName(e.target.value)} required />
          <Input
            label="Work email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Company" value={company} onChange={(e) => setCompany(e.target.value)} />
            <Input
              label="# of facilities"
              type="number"
              min={1}
              value={facilities}
              onChange={(e) => setFacilities(e.target.value)}
            />
          </div>
        </div>
        <Btn kind="primary" full type="submit" disabled={submitting}>
          {submitting ? "Sending…" : "Show me what I'd keep"}
        </Btn>
      </form>
    </Card>
  );
}
